import React from "react";
import { Card, Row, Col, Button, Upload, Tooltip, Spin } from "antd";
import {
  SmileOutlined,
  SyncOutlined,
  MessageOutlined,
  FileSyncOutlined,
  FileTextOutlined,
  UploadOutlined,
  DownloadOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import "Common/global.scss";
import useCompanyTemplates from "Hooks/AdminHooks/useCompanyTemplates";
import { TEMPLATE_ALLOWED_EXTENSIONS } from "Constants/Enums/HiringDocumentTemplateEnum";
import VersionHistoryModal from "./VersionHistoryModal";

const ACCEPT = TEMPLATE_ALLOWED_EXTENSIONS.join(",");

// Icon logic (unchanged from the original card design)
const getIconForItem = (title = "") => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes("recruit")) {
    if (lowerTitle.includes("spec")) return <SmileOutlined />;
    return <SyncOutlined />;
  } else if (lowerTitle.includes("offer")) {
    return <MessageOutlined />;
  } else if (lowerTitle.includes("performance") || lowerTitle.includes("report")) {
    return <FileSyncOutlined />;
  }
  return <FileTextOutlined />;
};

// Card gradient styles (unchanged green cards)
const cardClasses = [
  "doc-card-1",
  "doc-card-2",
  "doc-card-3",
  "doc-card-4",
  "doc-card-5",
  "doc-card-6",
  "doc-card-7",
  "doc-card-8",
];

// Company Documents keeps the original green gradient cards. Only the added
// feature (upload / download / replace / version history + version badge) is
// blended onto each card. Data comes from the backend template repository
// (per Mudassir Jul-15 handoff).
export default function CompanyDoc() {
  const {
    templates,
    loading,
    uploadingType,
    history,
    handleUploadFile,
    handleDownloadCurrent,
    openHistory,
    closeHistory,
    handleDownloadVersion,
  } = useCompanyTemplates();

  const uploadProps = (item, isBusy) => ({
    accept: ACCEPT,
    showUploadList: false,
    maxCount: 1,
    disabled: isBusy,
    beforeUpload: (file) => {
      handleUploadFile(item, file);
      return false; // handled manually; stop AntD auto-upload
    },
  });

  return (
    <Card title="Company Documents" bordered={false} className="card-professional">
      <Spin spinning={loading}>
        <div className="document-cards-container">
          <Row gutter={[20, 20]} justify="center">
            {templates.map((item, index) => {
              const cardClass = cardClasses[index % cardClasses.length];
              const isBusy = uploadingType === item.documentType;

              return (
                <Col xs={24} sm={12} md={8} lg={6} xl={6} key={item.documentType}>
                  <div className="company-doc-card">
                    <Card className={`pro-green-card ${cardClass}`}>
                      <div className="pro-card-content">
                        <div className="card-icon">{getIconForItem(item.displayName)}</div>
                        <h3>{item.displayName}</h3>

                        <div className="company-doc-meta">
                          <span className="company-doc-badge">
                            {item.isUploaded ? `v${item.version}` : "Not uploaded"}
                          </span>
                        </div>

                        <div className="company-doc-actions">
                          {item.isUploaded ? (
                            <>
                              <Tooltip title="Download">
                                <Button
                                  size="small"
                                  icon={<DownloadOutlined />}
                                  onClick={() => handleDownloadCurrent(item)}
                                />
                              </Tooltip>
                              <Upload {...uploadProps(item, isBusy)}>
                                <Tooltip title="Replace / New Version">
                                  <Button size="small" icon={<SyncOutlined />} loading={isBusy} />
                                </Tooltip>
                              </Upload>
                              <Tooltip title="Version History">
                                <Button
                                  size="small"
                                  icon={<HistoryOutlined />}
                                  onClick={() => openHistory(item)}
                                />
                              </Tooltip>
                            </>
                          ) : (
                            <Upload {...uploadProps(item, isBusy)}>
                              <Button size="small" icon={<UploadOutlined />} loading={isBusy}>
                                Upload
                              </Button>
                            </Upload>
                          )}
                        </div>
                      </div>
                    </Card>
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      </Spin>

      <VersionHistoryModal
        open={history.open}
        title={history.displayName}
        loading={history.loading}
        versions={history.versions}
        onClose={closeHistory}
        onDownload={handleDownloadVersion}
      />
    </Card>
  );
}
